import { Card } from "primereact/card";
import { useEffect, useMemo, useRef, useState } from "react";
import { Divider } from "primereact/divider";
import { ApiResponse } from "../../services/base/apiResponse";
import { PlanResponse, StudentInfoAdminRequest, StudentInfoAdminResponse, StudentResponse } from "../../models/dtos";
import * as Yup from "yup";
import { FormikProvider, useFormik, useFormikContext } from "formik";
import CustomInputText from "../../components/forms/CustomInputText";
import { identificationTypesApi } from "../../services/nomenclatures/identificationTypesApi";
import { countriesApi } from "../../services/nomenclatures/countriesApi";
import { sexesApi } from "../../services/nomenclatures/sexesApi";
import { citiesApi } from "../../services/nomenclatures/citiesApi";
import { Toast } from "primereact/toast";
import CustomDropdown from "../../components/forms/CustomDropdown";
import CustomDatepicker from "../../components/forms/CustomDatepicker";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import CustomCheckbox from "../../components/forms/CustomCheckbox";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import { studentsApi } from "../../services/students/studentsApi";
import FilePreviewDialog from "../../components/common/FilePreviewDialog";

interface PersonalDataProps {
  student?: ApiResponse<StudentInfoAdminResponse>;
  passportFile?: File | null;
  setPassportFile?: (file: File | null) => void;
  isCreatingStudent?: boolean;
}

const PersonalData = ({ student, passportFile, setPassportFile, isCreatingStudent = false }: PersonalDataProps) => {
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<any>(null);

  const { data: identifications } = identificationTypesApi.useGetIdentificationTypesQuery();
  const { data: nationalities } = countriesApi.useGetCountriesQuery();
  const { data: genders } = sexesApi.useGetSexesQuery();
  const { data: countries } = countriesApi.useGetCountriesQuery();
  const { data: cities } = citiesApi.useGetCitiesQuery();
  //const { data: eduForm, error, isLoading } = specialityModesApi.useGetSpecialityModesQuery();

  const [uploadPassport, { isLoading: isUploadingPassport }] = studentsApi.useUploadPassportMutation();
  const [triggerDownloadPassport, { isLoading: isDownloadingPassport }] = studentsApi.useLazyDownloadPassportQuery();

  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
    if (setPassportFile && !isCreatingStudent) {
      setPassportFile(null);
    }
  }, [student?.data?.studentId]);

  const openPdfInModal = async (studentId: number) => {
    try {
      const result = (await triggerDownloadPassport(studentId).unwrap()) as any;
      console.log("Downloaded Passport:", result);
      const url = URL.createObjectURL(result.blob as Blob);
      setPdfUrl(url);
      setShowPdfDialog(true);
    } catch (error) {
      console.error("Error downloading passport:", error);
      toast.current!.show({
        severity: "error",
        summary: "Грешка",
        detail: "Възникна грешка при изтеглянето на паспорта.",
      });
    }
  };

  const closePdfDialog = () => {
    setShowPdfDialog(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const onUpload = (event: FileUploadHandlerEvent) => {
    console.log("onUpload called, isCreatingStudent:", isCreatingStudent, "file:", event.files[0]);
    if (isCreatingStudent && setPassportFile) {
      console.log("Setting passport file in state:", event.files[0]);
      setPassportFile(event.files[0]);
      toast.current!.show({
        severity: "info",
        summary: "Информация",
        detail: "Паспортът ще бъде качен при запазване на студента.",
      });
    } else if (student?.data.studentId) {
      console.log("Uploading passport immediately for existing student");
      uploadPassport({ studentId: student.data.studentId, passport: event.files[0] }).then((response) => {
        if ("data" in response && response.data) {
          toast.current!.show({ severity: "success", summary: "Успешно", detail: "Паспортът е качен." });
        } else {
          toast.current!.show({
            severity: "error",
            summary: "Грешка",
            detail: "Паспортът не е качен, моля проверете файла.",
          });
        }
      });
    }
  };

  const planTemplate = (rowData: StudentResponse) => {
    return (
      <div className="flex align-items-center">
        <Link to={`/students/${rowData.studentId}`}>
          <Button outlined>{rowData.facultyNumber}</Button>
        </Link>
      </div>
    );
  };

  const formik = useFormikContext<any>();

  return (
    <div className="flex justify-content-center flex-wrap">
      <Card className="w-full mt-4">
        <Divider />
        <div className="col-12">
          <div className="card">
            <div className="p-fluid formgrid grid">
              <div className="col-4">
                <CustomInputText id="firstName" label="Име" className="mr-2" required />
              </div>
              <div className="col-4">
                <CustomInputText id="middleName" label="Презиме" className="mr-2" />
              </div>
              <div className="col-4">
                <CustomInputText id="lastName" label="Фамилия" className="mr-2" required />
              </div>
              <div className="col-4">
                <CustomInputText id="firstNameEn" label="Име (на английски)" className="mr-2" required />
              </div>
              <div className="col-4">
                <CustomInputText id="middleNameEn" label="Презиме (на английски)" className="mr-2" />
              </div>
              <div className="col-4">
                <CustomInputText id="lastNameEn" label="Фамилия (на английски)" className="mr-2" required />
              </div>
              {student?.data?.studentId && (
                <div className="field col-12">
                  <CustomInputText id="userEmail" label="Студентски и-мейл" labelIcon="pi pi-exclamation-triangle" />
                  {!formik.values.userEmail?.endsWith("@trakia-uni.bg") && (
                    <div className="text-yellow-300 text-sm">
                      Студентският и-мейл трябва да завършва на @trakia-uni.bg
                    </div>
                  )}
                  <div className="text-gray-500 text-sm">
                    Ако този и-мейл все още не е създаден, можете да натиснете бутона "Генериране на студентски и-мейл"
                    в горната част на екрана
                  </div>
                </div>
              )}
              <div className="field col-12 md:col-6">
                <CustomInputText id="email" label="Личен E-mail" required />
              </div>
              <div className="field col-12 md:col-3">
                <CustomInputText id="phone" label="Телефонен номер" required />
              </div>
              <div className="field col-12 md:col-3">
                <CustomInputText id="secondPhone" label="Телефонен номер на близък" />
              </div>
              <Divider />
              <div className="field col-12 md:col-4">
                <CustomDropdown
                  id="citizenshipCountryId"
                  label="Гражданство"
                  options={nationalities?.data ?? []}
                  optionLabel="name"
                  optionValue="countryId"
                  placeholder="Изберете опция"
                  required
                  filter
                  showClear
                />
              </div>

              <div className="field col-12 md:col-1 flex align-items-center">
                <CustomCheckbox id="isForeigner" label="Чужденец" />
              </div>

              <div className="field col-12 md:col-2">
                <CustomDropdown
                  id="identificationTypeId"
                  label="Идентификационен тип"
                  options={identifications?.data ?? []}
                  optionLabel="name"
                  optionValue="identificationTypeId"
                  placeholder="Изберете опция"
                  required
                />
              </div>

              <div className="field col-12 md:col-3">
                <CustomInputText id="egn" label="Идентификационен номер" required />
              </div>

              <div className="field col-12 md:col-1">
                <CustomDropdown
                  id="sexId"
                  label="Пол"
                  options={genders?.data ?? []}
                  optionLabel="name"
                  optionValue="sexId"
                  placeholder="Изберете опция"
                  required
                />
              </div>

              <div className="field col-12 md:col-2">
                <CustomDropdown
                  id="secondaryCitizenshipCountryId"
                  label="Второ гражданство"
                  options={nationalities?.data ?? []}
                  optionLabel="name"
                  optionValue="countryId"
                  placeholder="Изберете опция"
                  filter
                  showClear
                />
              </div>

              <div className="field col-12 md:col-2">
                <CustomDropdown
                  id="secondaryIdentificationTypeId"
                  label="Втори идентификатор"
                  options={identifications?.data ?? []}
                  optionLabel="name"
                  optionValue="identificationTypeId"
                  placeholder="Изберете опция"
                  filter
                />
              </div>
              <div className="field col-12 md:col-3">
                <CustomInputText id="secondaryIdentifier" label="Втори идентификационен номер" />
              </div>

              <div className="field col-12 md:col-3 flex flex-row">
                <div className="flex flex-column gap-2 mb-2">
                  <label>Паспорт</label>
                  <FileUpload
                    ref={fileUploadRef}
                    mode="basic"
                    name="passportFile"
                    accept="image/*,application/pdf"
                    maxFileSize={5000000}
                    customUpload
                    auto
                    chooseLabel={isUploadingPassport ? "Качване..." : passportFile ? "Файл избран" : "Качи файл"}
                    uploadHandler={onUpload}
                    chooseOptions={{
                      className: "p-button-outlined p-button-secondary",
                      icon: isUploadingPassport
                        ? "pi pi-spinner pi-spin"
                        : passportFile
                          ? "pi pi-check"
                          : "pi pi-upload",
                    }}
                  />
                </div>
                {formik.values.hasPassportFile && (
                  <div className="flex text-gray-500 text-sm">
                    <Button
                      link
                      onClick={() => openPdfInModal(student?.data.studentId!)}
                      loading={isDownloadingPassport}
                    >
                      <i className="pi pi-download mr-2"></i>
                      Преглед
                    </Button>
                  </div>
                )}
              </div>

              <div className="field col-12 md:col-3">
                <CustomInputText id="visaDNumber" label="Виза D номер" />
              </div>
              <div className="field col-12 md:col-3">
                <CustomDatepicker id="visaDDate" label="Виза D дата" />
              </div>

              <Divider />
              <div className="col-12">
                <h4>Месторождение</h4>
              </div>
              <div className="field col-12 md:col-5">
                <CustomDropdown
                  filter
                  id="birthCountryId"
                  label="Държава"
                  options={countries?.data ?? []}
                  optionLabel="name"
                  optionValue="countryId"
                  placeholder="Изберете опция"
                  required
                  showClear
                />
              </div>
              <div className="field col-12 md:col-3">
                <CustomDropdown
                  filter
                  id="birthCityId"
                  label="Град"
                  options={cities?.data ?? []}
                  optionLabel={(option) => `${option.name} (общ. ${option.municipalityName})`}
                  optionValue="cityId"
                  placeholder="Изберете опция"
                  showClear
                />
              </div>
              <div className="field col-12 md:col-2">
                <CustomInputText id="birthCityText" label="Град (свободен текст, за чужбина)" />
              </div>
              <div className="field col-12 md:col-2">
                <CustomDatepicker id="birthDate" label="Дата на раждане" required />
              </div>
              <div className="col-12">
                <h4>Постоянно местоживеене</h4>
              </div>
              <div className="field col-12 md:col-6">
                <CustomDropdown
                  filter
                  id="permanentCountryId"
                  label="Държава"
                  options={countries?.data ?? []}
                  optionLabel="name"
                  optionValue="countryId"
                  placeholder="Изберете опция"
                  required
                  showClear
                />
              </div>
              <div className="field col-12 md:col-3">
                <CustomDropdown
                  filter
                  id="permanentCityId"
                  label="Град"
                  options={cities?.data ?? []}
                  optionLabel={(option) => `${option.name} (общ. ${option.municipalityName})`}
                  optionValue="cityId"
                  placeholder="Изберете опция"
                  showClear
                />
              </div>
              <div className="field col-12 md:col-3">
                <CustomInputText id="permanentCityText" label="Град (свободен текст, за чужбина)" />
              </div>
              <div className="field col-12">
                <CustomInputText id="permanentAddress" label="Адрес" type="multiline" required />
              </div>
              <div className="col-12">
                <h4>Текущо местоживеене</h4>
              </div>
              <div className="field col-12 md:col-6">
                <CustomDropdown
                  filter
                  id="currentCountryId"
                  label="Държава"
                  options={countries?.data ?? []}
                  optionLabel="name"
                  optionValue="countryId"
                  placeholder="Изберете опция"
                  showClear
                />
              </div>
              <div className="field col-12 md:col-3">
                <CustomDropdown
                  filter
                  id="currentCityId"
                  label="Град"
                  options={cities?.data ?? []}
                  optionLabel={(option) => `${option.name} (общ. ${option.municipalityName})`}
                  optionValue="cityId"
                  placeholder="Изберете опция"
                  showClear
                />
              </div>
              <div className="field col-12 md:col-3">
                <CustomInputText id="currentCityText" label="Град (свободен текст, за чужбина)" />
              </div>
              <div className="field col-12">
                <CustomInputText id="currentAddress" label="Адрес" type="multiline" />
              </div>

              <div className="col-12">
                <h4>Лична карта / паспорт</h4>
              </div>
              <div className="field col-12 md:col-6">
                <CustomInputText id="passportNumber" label="Номер" />
              </div>
              <div className="field col-12 md:col-6">
                <CustomDatepicker id="passportIssuedOn" label="Издадена на" />
              </div>
              <div className="field col-12 md:col-6">
                <CustomDropdown
                  filter
                  id="passportCityId"
                  label="Място на издаване"
                  options={cities?.data ?? []}
                  optionLabel={(option) => `${option.name} (общ. ${option.municipalityName})`}
                  optionValue="cityId"
                  placeholder="Изберете опция"
                  showClear
                />
              </div>
              <div className="field col-12 md:col-6">
                <CustomInputText id="passportCityText" label="Място на издаване (свободен текст, за чужбина)" />
              </div>
            </div>
          </div>
        </div>
      </Card>

      <FilePreviewDialog visible={showPdfDialog} onHide={closePdfDialog} fileUrl={pdfUrl} title="Паспорт" />
    </div>
  );
};

export default PersonalData;
